import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import CourseDetails from '../../components/Course/CourseDetails';
import Breadcrumb from '../../components/Breadcrumb';
import { getCourse } from '../../services/courseService';
import { Course } from '../../types/interfaces';
import { Container, CircularProgress, Typography, Button, Box } from '@mui/material';
import LessonList from '../../components/Lesson/LessonList';  // Import the LessonList component
import AddIcon from '@mui/icons-material/Add';
import AuthoringAssistantPanel from '../../components/AI/AuthoringAssistantPanel';
import { useAuth } from '../../components/AuthContext';
import { UserRole } from '../../common/enums';
import { buildAuthoringAssistantCreateState } from '../../components/AI/authoringAssistantNavigation';

const CourseViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const canUseAuthoringAssistant = user?.role === UserRole.Admin || user?.role === UserRole.Tutor;

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        const response = await getCourse(parseInt(id, 10));
        setCourse(response.data);
      }
      setLoading(false);
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  if (!course) {
    return (
      <Container>
        <Typography variant="h5" component="div">
          Kurssia ei löytynyt
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          flexDirection: { xs: 'column', xl: 'row' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {id && <Breadcrumb courseId={id} />}

          <Box mb={3}>
            <CourseDetails course={course} />
          </Box>

          <Box mb={4}>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to={`/course/${id}/lesson/create`}
              sx={{ textAlign: 'left' }}
              startIcon={<AddIcon />}
            >
              Lisää uusi oppitunti
            </Button>
          </Box>

          <Box mb={6}>
            {id && <LessonList courseId={id} />}
          </Box>
        </Box>

        {canUseAuthoringAssistant && id && (
          <AuthoringAssistantPanel
            courseId={parseInt(id, 10)}
            draftCourse={{
              name: course.name,
              description: course.description,
              startDate: course.startDate,
              endDate: course.endDate,
              subject: course.subject,
              subjectSpecification: course.subjectSpecification,
            }}
            createActions={[
              {
                id: 'create-lesson',
                label: 'Aloita uusi oppitunti',
                onCreate: (prompt) => {
                  navigate(`/course/${id}/lesson/create`, {
                    state: buildAuthoringAssistantCreateState('lesson', prompt),
                  });
                },
              },
            ]}
          />
        )}
      </Box>
    </Container>
  );
};

export default CourseViewPage;
